/* ==========================================================================
   PDP Redesign — option picker enhancement

   Progressive enhancement, not a replacement: the Product Customizer app's
   <select> elements stay in the DOM exactly as it renders them (same name,
   same options, same values) and remain the source of truth for price
   calculation and required-field validation. This script only adds a
   visual picker (segmented pill buttons) on top of each select, and keeps
   both in sync:
     - clicking a button sets select.value and dispatches a bubbling
       'change' event, so the app's own price/validation logic runs exactly
       as it would for a native dropdown interaction.
     - if the app (or anything else) changes the select's value or shows
       its own red-border validation error, the picker's visual state is
       updated to match.
   Every field — including Colours — renders as a named text button, not a
   colour swatch, per request.
   If anything about a field looks unexpected (no options, can't be read
   safely) this script leaves that field as the app's native dropdown
   rather than guessing.

   Also enforces "length-restricted" options (see
   applyLengthRestrictedOptions below) — e.g. Size options labelled
   "... Below Knee Only" or "... Knee High Only" that the Product
   Customizer app has no way to disable on its own.

   Also enforces "Requires ..." options (see applyRequiredAddonOptions
   below) — e.g. SIGVARIS Advance sizes labelled "M Plus (Requires
   Sensinova Grip Top)" auto-select "WITH Sensinova Grip Top" and lock out
   "WITHOUT", since the app can't set one field's value from another.

   Also enforces "(X not available)" options (folded into
   applyLengthRestrictedOptions above) — e.g. JUZO Soft/Dynamic's Length
   "Extra Short (Pantyhose not available)" disables that Length option
   whenever Model is currently "Pantyhose".

   Also enforces "class-restricted" options (see
   applyClassRestrictedOptions below) — e.g. SIGVARIS Cotton Essential's
   Length "Short (Only available in class 2)" that the Product Customizer
   app has no way to disable on its own.

   And hardcodes the Product Customizer app's own field-level show/hide
   conditional logic (see PRODUCT_RULES / applyProductCustomizerRules
   below) as a transparent, in-theme copy of those rules, keyed by
   product handle — see the comment above PRODUCT_RULES for why.
   ========================================================================== */
(function () {
  'use strict';

  // Finds a " - " that separates a label from a trailing note (e.g.
  // "Black - Classes 1 and 2 only"), but ignores one that falls inside
  // parentheses (e.g. the range in "Class 1 (CCL 1: 15 - 20 mmHg)") so a
  // numeric range never gets mistaken for a note delimiter.
  function splitLabelNote(text) {
    var depth = 0;
    for (var i = 0; i < text.length - 2; i++) {
      var ch = text[i];
      if (ch === '(') depth++;
      else if (ch === ')') depth--;
      else if (depth === 0 && ch === ' ' && text[i + 1] === '-' && text[i + 2] === ' ') {
        return { label: text.slice(0, i).trim(), note: text.slice(i + 3).trim() };
      }
    }
    return { label: text, note: null };
  }

  // "Thigh High (+$100.00)" -> { label: "Thigh High", price: "+$100" }
  // "Black - Classes 1 and 2 only (+$0.00)" -> { label: "Black", note: "Classes 1 and 2 only", price: null }
  // "Class 1 (CCL 1: 15 - 20 mmHg) (+$0.00)" -> { label: "Class 1 (CCL 1: 15 - 20 mmHg)", note: null, price: null }
  function parseOptionText(text) {
    var priceMatch = text.match(/\(([+-]\$[\d,]+(?:\.\d+)?)\)\s*$/);
    var price = null;
    var rest = text;
    if (priceMatch) {
      var amount = priceMatch[1].replace(/\.00$/, '');
      if (!/^[+-]\$0$/.test(amount)) price = amount;
      rest = text.slice(0, priceMatch.index).trim();
    }
    var split = splitLabelNote(rest);
    var label = split.label;
    var note = split.note;

    // A short clinical/technical detail often trails the label in its own
    // parentheses, e.g. "Class 1 (CCL 1: 15 - 20 mmHg)". Move it into the
    // tooltip so the button itself stays a short, tappable label.
    if (!note) {
      var trailingParen = label.match(/^(.+?)\s*\(([^()]+)\)\s*$/);
      if (trailingParen && trailingParen[1].trim()) {
        label = trailingParen[1].trim();
        note = trailingParen[2].trim();
      }
    }

    return { label: label, note: note, price: price };
  }

  function buildPicker(select, fieldCard) {
    var wrap = document.createElement('div');
    wrap.className = 'pdp-seg-row';
    wrap.setAttribute('role', 'group');

    // First pass: parse every real option up front so we can detect when
    // the short label would collide across multiple options — e.g. three
    // separate "Thigh High ... (+$75.00)" options that only differ in their
    // parenthetical detail ("With Lace Sensitive...", "With Silicone
    // Dots...", "With Silicone Dot Pattern...") would otherwise all render
    // as an identical "Thigh High +$75" button with the only distinguishing
    // text tucked into a hover-only title tooltip — invisible on mobile and
    // easy to miss on desktop, so a shopper can't actually tell the options
    // apart. When a label+price combo isn't unique, keep that detail on the
    // visible button instead of demoting it to a tooltip; a non-colliding
    // option (e.g. "Class 1 (CCL 1: 15 - 20 mmHg)") still gets the clean
    // short label with the detail as a tooltip, same as before.
    var realOptions = Array.prototype.filter.call(select.options, function (opt) {
      return !!opt.value;
    });
    var parsedList = realOptions.map(function (opt) {
      return { opt: opt, parsed: parseOptionText(opt.text) };
    });
    var labelCounts = {};
    parsedList.forEach(function (item) {
      var key = item.parsed.label + '|' + (item.parsed.price || '');
      labelCounts[key] = (labelCounts[key] || 0) + 1;
    });

    var buttons = [];
    parsedList.forEach(function (item) {
      var opt = item.opt;
      var parsed = item.parsed;
      var key = parsed.label + '|' + (parsed.price || '');
      var isAmbiguous = !!parsed.note && labelCounts[key] > 1;

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'pdp-seg-btn';
      btn.dataset.value = opt.value;

      var labelSpan = document.createElement('span');
      labelSpan.textContent = isAmbiguous ? (parsed.label + ' (' + parsed.note + ')') : parsed.label;
      btn.appendChild(labelSpan);

      if (parsed.price) {
        var priceSpan = document.createElement('span');
        priceSpan.className = 'pdp-seg-btn__price';
        priceSpan.textContent = ' ' + parsed.price;
        btn.appendChild(priceSpan);
      }
      if (parsed.note && !isAmbiguous) btn.title = parsed.note;

      btn.addEventListener('click', function () {
        if (btn.disabled) return;
        if (select.value === opt.value) return;
        select.value = opt.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      });

      wrap.appendChild(btn);
      buttons.push({ btn: btn, opt: opt, value: opt.value });
    });

    function syncActive() {
      buttons.forEach(function (b) {
        var active = b.value === select.value;
        b.btn.classList.toggle('is-active', active);
        b.btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }
    syncActive();
    select.addEventListener('change', syncActive);

    // Mirror the app's own red-border validation error (set directly on the
    // select's style attribute) onto the picker, so the same visual cue
    // shows on the buttons a shopper actually clicks.
    var errorObserver = new MutationObserver(function () {
      var hasError = !!(select.style.border && select.style.border.indexOf('d32f2f') !== -1);
      fieldCard.classList.toggle('has-error', hasError);
    });
    errorObserver.observe(select, { attributes: true, attributeFilter: ['style'] });

    return { wrap: wrap, buttons: buttons };
  }

  function enhanceField(fieldCard) {
    if (fieldCard.dataset.pdpEnhanced) return;
    var select = fieldCard.querySelector('select');
    if (!select || select.options.length < 2) return; // nothing to build a picker from

    var picker = buildPicker(select, fieldCard);

    // Visually hide the select but keep it in-flow, in place, and fully
    // functional — this is what the app's price/validation logic reads,
    // and what the existing "scroll to the missing field" behaviour targets.
    select.classList.add('pdp-visually-hidden-select');

    select.insertAdjacentElement('afterend', picker.wrap);
    fieldCard.dataset.pdpEnhanced = '1';
    fieldCard.pdpButtons = picker.buttons; // stashed for applyLengthRestrictedOptions
  }

  function enhanceAll() {
    var fields = document.querySelectorAll('product-info .product-customizer-option');
    Array.prototype.forEach.call(fields, enhanceField);
  }

  /* ------------------------------------------------------------------------
     Length-restricted options
     The Product Customizer app's conditional-logic can only show/hide an
     entire field, never a single option inside one — so options like
     JOBST's "LFC - Below Knee Only" / "XLCF - Below Knee Only" render as
     selectable no matter what Length is chosen, even though the option's
     own label says otherwise.

     Rather than requiring a duplicate field + app-admin setup per product,
     this reads that same "... Below Knee Only" / "... Knee High Only"
     wording already present in the option text — a convention already used
     across the catalog — and enforces it generically:
       1. Find the field that looks like a Length/Model selector (it has a
          "Below Knee" option alongside a "Thigh High" and/or "Pantyhose"
          option).
       2. In every OTHER field on the page, find options whose text contains
          "... Only" phrasing tied to Below Knee / Knee High.
       3. Disable those options (both the native <option> and its picker
          button) whenever the Length field's current value isn't a Below
          Knee variant, and clear the selection if it's no longer valid.
     Also enforces the inverse wording — an option elsewhere that names one
     of THIS field's own values as excluded, e.g. JUZO Soft/Dynamic's Length
     "Extra Short (Pantyhose not available)" excluding Model "Pantyhose":
     disabled whenever the Length/Model field's current selection matches
     the named value.
     No product-specific configuration needed — this runs on every PDP and
     only ever does anything on products that actually use this wording.
     ------------------------------------------------------------------------ */
  function looksLikeBelowKnee(text) {
    // "Below Knee" is the common wording, but JOBST FORMEN uses "Knee High"
    // / "Casual Knee High" for the same below-knee-length concept (its
    // Model field has no "Below Knee" option at all) — treated as
    // equivalent so its "... Below Knee Only" sizes get cross-restricted
    // too. Checked against every other product's fields (Sep 2026 catalog
    // audit): no other product has a Length/Model-like field that gains a
    // false match from this broadening.
    return /below knee|\bknee high\b/i.test(text || '');
  }

  function looksLikeOtherLength(text) {
    return /thigh high|pantyhose/i.test(text || '');
  }

  function isKneeOnlyOption(text) {
    // Matches "... Below Knee Only", "... Knee High Only", "... (Extra Large
    // Full Calf - Below Knee Only" (even with the store's occasional missing
    // closing parenthesis), etc. — any option text mentioning both "knee"
    // and "only" is treated as knee-length-restricted.
    var t = (text || '').toLowerCase();
    return t.indexOf('knee') !== -1 && t.indexOf('only') !== -1;
  }

  // "Extra Short (Pantyhose not available)" -> "Pantyhose" — the inverse of
  // the knee-only pattern above: an option on ONE field (often Length)
  // saying it can't be combined with a particular value of the Length/Model
  // field itself (e.g. JUZO Soft/Dynamic's Length "Extra Short" can't be
  // combined with Model "Pantyhose"). Only ever matched against the
  // Length/Model field's own option labels (see applyLengthRestrictedOptions
  // below), so it can't misfire on unrelated "(... not available)" wording.
  function unavailableWhenText(text) {
    var m = /\(([^()]+?)\s+not available\)/i.exec(text || '');
    return m ? m[1].trim() : null;
  }

  function findLengthSelect(fieldCards) {
    for (var i = 0; i < fieldCards.length; i++) {
      var select = fieldCards[i].querySelector('select');
      if (!select) continue;
      var hasBelowKnee = false;
      var hasOtherLength = false;
      Array.prototype.forEach.call(select.options, function (opt) {
        if (looksLikeBelowKnee(opt.text)) hasBelowKnee = true;
        if (looksLikeOtherLength(opt.text)) hasOtherLength = true;
      });
      if (hasBelowKnee && hasOtherLength) return select;
    }
    return null;
  }

  function applyLengthRestrictedOptions() {
    if (document.body.dataset.pdpLengthRestrictionWired) return;

    var fieldCards = document.querySelectorAll('product-info .product-customizer-option');
    var lengthSelect = findLengthSelect(fieldCards);
    if (!lengthSelect) return;

    // The Length/Model field's own option labels (price/notes stripped),
    // lowercased — used below to resolve "(X not available)" wording on
    // OTHER fields back to one of this field's own values.
    var lengthOptionLabels = {};
    Array.prototype.forEach.call(lengthSelect.options, function (opt) {
      if (!opt.value) return;
      var label = parseOptionText(opt.text).label.trim().toLowerCase();
      if (label) lengthOptionLabels[label] = true;
    });

    var restricted = [];
    var restrictedByValue = []; // { select, opt, btn, forbidden } — "(X not available)"
    Array.prototype.forEach.call(fieldCards, function (fieldCard) {
      var select = fieldCard.querySelector('select');
      if (!select || select === lengthSelect || !fieldCard.pdpButtons) return;
      fieldCard.pdpButtons.forEach(function (b) {
        if (isKneeOnlyOption(b.opt.text)) {
          restricted.push({ select: select, opt: b.opt, btn: b.btn });
        }
        var forbidden = unavailableWhenText(b.opt.text);
        if (forbidden && lengthOptionLabels[forbidden.toLowerCase()]) {
          restrictedByValue.push({ select: select, opt: b.opt, btn: b.btn, forbidden: forbidden.toLowerCase() });
        }
      });
    });
    if (!restricted.length && !restrictedByValue.length) return;

    function sync() {
      var selectedOpt = lengthSelect.options[lengthSelect.selectedIndex];
      var selectedText = selectedOpt ? selectedOpt.text : '';
      var selectedLabel = selectedOpt ? parseOptionText(selectedOpt.text).label.trim().toLowerCase() : '';

      // Nothing chosen yet: leave restricted options enabled rather than
      // showing a field that looks broken before the shopper has picked a
      // Length at all. Once a definite non-Below-Knee Length is chosen,
      // restricted options are disabled (and cleared if selected).
      var allowed = !lengthSelect.value || looksLikeBelowKnee(selectedText);
      restricted.forEach(function (r) {
        r.opt.disabled = !allowed;
        if (r.btn) {
          r.btn.disabled = !allowed;
          r.btn.classList.toggle('pdp-seg-btn--unavailable', !allowed);
          r.btn.title = allowed ? '' : 'Only available for Below Knee length';
        }
        if (!allowed && r.select.value === r.opt.value) {
          r.select.value = '';
          r.select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });

      restrictedByValue.forEach(function (r) {
        var blocked = !!lengthSelect.value && selectedLabel === r.forbidden;
        r.opt.disabled = blocked;
        if (r.btn) {
          r.btn.disabled = blocked;
          r.btn.classList.toggle('pdp-seg-btn--unavailable', blocked);
          r.btn.title = blocked ? 'Not available with ' + parseOptionText(selectedText).label : '';
        }
        if (blocked && r.select.value === r.opt.value) {
          r.select.value = '';
          r.select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }

    lengthSelect.addEventListener('change', sync);
    sync();
    document.body.dataset.pdpLengthRestrictionWired = '1';
  }

  /* ------------------------------------------------------------------------
     "Requires ..." options
     Some options say in their own label that they need an add-on chosen in
     another field — e.g. SIGVARIS Advance Armsleeve sizes
     "M Plus (Requires Sensinova Grip Top)", where a separate "Sensinova
     Grip Top" field offers "WITH Sensinova Grip Top (+$15.00)" /
     "WITHOUT Sensinova Grip Top". The Product Customizer app can only
     show/hide whole fields, never set one field's value from another, so
     this does it, generically, from that wording:
       - choosing an option that says "(Requires X)" auto-selects the
         "WITH X" option in whichever field offers WITH X / WITHOUT X, and
         locks out "WITHOUT X" (native <option> and its picker button),
         with a short visible note saying why;
       - choosing an option without that wording (e.g. a standard size)
         unlocks "WITHOUT X" again so the shopper can pick either. If
         "WITH X" was only there because this rule put it there (the
         shopper never chose it themselves), it's cleared back to "Please
         choose one" rather than silently keeping a paid add-on they didn't
         pick — the field is required, so the app's own validation will ask
         for a choice. A "WITH X" the shopper picked themselves is kept.
     Values are changed by dispatching a bubbling 'change' event, exactly
     like a picker click, so the app's price total and cart properties
     update as normal. Fields are only paired within the same <form>: the
     app renders a second, hidden copy of every field inside the product
     form, and each copy is handled on its own.
     Only does anything on products whose option text uses this wording.
     ------------------------------------------------------------------------ */
  function squashOptionText(text) {
    return normalizeOptionText(text).replace(/\s+/g, ' ').toLowerCase();
  }

  // "M Plus (Requires Sensinova Grip Top)" -> "Sensinova Grip Top"
  function requiredAddonName(text) {
    var m = /\brequires\s+([^()]+?)\s*(?:\)|$)/i.exec(normalizeOptionText(text));
    return m ? m[1].replace(/\s+/g, ' ').trim() : null;
  }

  function findAddonField(scope, sourceSelect, addonName) {
    var want = addonName.toLowerCase();
    var selects = scope.querySelectorAll('.product-customizer-option select');
    for (var i = 0; i < selects.length; i++) {
      var select = selects[i];
      if (select === sourceSelect) continue;
      var withOpt = null;
      var withoutOpt = null;
      Array.prototype.forEach.call(select.options, function (opt) {
        var t = squashOptionText(opt.text);
        if (t === 'with ' + want) withOpt = opt;
        else if (t === 'without ' + want) withoutOpt = opt;
      });
      if (withOpt && withoutOpt) {
        return { select: select, withOpt: withOpt, withoutOpt: withoutOpt };
      }
    }
    return null;
  }

  function setAddonNote(card, text) {
    var note = card.querySelector('.pdp-seg-note');
    if (!text) {
      if (note) note.hidden = true;
      return;
    }
    if (!note) {
      note = document.createElement('p');
      note.className = 'pdp-seg-note';
      note.setAttribute('aria-live', 'polite');
      var row = card.querySelector('.pdp-seg-row');
      if (row) row.insertAdjacentElement('afterend', note);
      else card.appendChild(note);
    }
    if (note.textContent !== text) note.textContent = text;
    note.hidden = false;
  }

  function applyRequiredAddonOptions() {
    var sources = document.querySelectorAll('product-info .product-customizer-option select');
    Array.prototype.forEach.call(sources, function (source) {
      if (source.dataset.pdpRequiresWired) return;

      var addonNames = {};
      Array.prototype.forEach.call(source.options, function (opt) {
        var name = requiredAddonName(opt.text);
        if (name) addonNames[name.toLowerCase()] = name;
      });
      if (!Object.keys(addonNames).length) return;

      var scope = source.form || source.closest('.product-customizer-content') || document;
      // Don't wire until at least one matching WITH/WITHOUT field has been
      // rendered — the MutationObserver re-runs this as the app's fields
      // arrive.
      var anyFound = Object.keys(addonNames).some(function (key) {
        return !!findAddonField(scope, source, addonNames[key]);
      });
      if (!anyFound) return;

      function sync() {
        var selected = source.options[source.selectedIndex];
        var needed = selected && selected.value ? requiredAddonName(selected.text) : null;
        var neededKey = needed ? needed.toLowerCase() : null;
        var sourceLabel = selected ? parseOptionText(normalizeOptionText(selected.text)).label : '';

        Object.keys(addonNames).forEach(function (key) {
          // Re-resolved on every change (cheap) rather than cached, in case
          // the app ever re-renders the add-on field.
          var field = findAddonField(scope, source, addonNames[key]);
          if (!field) return;
          var required = key === neededKey;
          var card = field.select.closest('.product-customizer-option');

          field.withoutOpt.disabled = required;
          var withoutBtn = null;
          if (card && card.pdpButtons) {
            card.pdpButtons.forEach(function (b) {
              if (b.opt === field.withoutOpt) withoutBtn = b.btn;
            });
          }
          if (withoutBtn) {
            withoutBtn.disabled = required;
            withoutBtn.classList.toggle('pdp-seg-btn--unavailable', required);
            withoutBtn.title = required ? 'Not available — ' + sourceLabel + ' requires the ' + addonNames[key] : '';
          }
          if (card) {
            setAddonNote(card, required
              ? 'Included — ' + sourceLabel + ' requires the ' + addonNames[key] + '.'
              : '');
          }

          if (required) {
            if (field.select.value !== field.withOpt.value) {
              field.select.value = field.withOpt.value;
              field.select.dataset.pdpAutoSelected = '1';
              field.select.dispatchEvent(new Event('change', { bubbles: true }));
            }
          } else if (field.select.dataset.pdpAutoSelected) {
            delete field.select.dataset.pdpAutoSelected;
            if (field.select.value === field.withOpt.value) {
              field.select.value = '';
              field.select.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        });
      }

      source.addEventListener('change', sync);
      source.dataset.pdpRequiresWired = '1';
      sync();
    });
  }

  /* ------------------------------------------------------------------------
     Class-restricted options
     Some options only exist in one Class of compression, stated only in the
     option's own label — e.g. SIGVARIS Cotton Essential's Length "Short
     (Only available in class 2)" — with no field to show/hide, since the
     restriction is on a specific OPTION within the Class field's sibling,
     not a whole field. Generic, content-driven, same approach as the
     length-restriction block above:
       1. Find the field that looks like a Class selector (every real option
          starts with "Class <number>").
       2. In every OTHER field, find options whose text says "(Only
          available in class N)" / "(Only available in classes N and M)".
       3. Disable those options whenever the Class field's current value
          isn't one of the named classes, clearing the selection if it's no
          longer valid.
     No product-specific configuration needed — only ever does anything on
     products that actually use this wording.
     ------------------------------------------------------------------------ */
  function looksLikeClassSelect(select) {
    var real = Array.prototype.filter.call(select.options, function (o) { return !!o.value; });
    if (!real.length) return false;
    return real.every(function (o) { return /^\s*class\s*\d/i.test(o.text); });
  }

  function findClassSelect(fieldCards) {
    for (var i = 0; i < fieldCards.length; i++) {
      var select = fieldCards[i].querySelector('select');
      if (select && looksLikeClassSelect(select)) return select;
    }
    return null;
  }

  // "Short (Only available in class 2)" -> [2]
  // "... (Only available in classes 1 and 2)" -> [1, 2]
  function requiredClassNumbers(text) {
    var m = /\bonly available in\s+class(?:es)?\s+([^)]*)/i.exec(text || '');
    if (!m) return null;
    var nums = m[1].match(/\d+/g);
    return nums && nums.length ? nums.map(Number) : null;
  }

  // "Class 2 - 23-32mmHG" -> 2
  function selectedClassNumber(text) {
    var m = /^\s*class\s*(\d+)/i.exec(text || '');
    return m ? Number(m[1]) : null;
  }

  function applyClassRestrictedOptions() {
    if (document.body.dataset.pdpClassRestrictionWired) return;

    var fieldCards = document.querySelectorAll('product-info .product-customizer-option');
    var classSelect = findClassSelect(fieldCards);
    if (!classSelect) return;

    var restricted = [];
    Array.prototype.forEach.call(fieldCards, function (fieldCard) {
      var select = fieldCard.querySelector('select');
      if (!select || select === classSelect || !fieldCard.pdpButtons) return;
      fieldCard.pdpButtons.forEach(function (b) {
        var need = requiredClassNumbers(b.opt.text);
        if (need) restricted.push({ select: select, opt: b.opt, btn: b.btn, need: need });
      });
    });
    if (!restricted.length) return;

    function sync() {
      var selectedOpt = classSelect.options[classSelect.selectedIndex];
      var currentClass = selectedOpt ? selectedClassNumber(selectedOpt.text) : null;
      restricted.forEach(function (r) {
        var allowed = !classSelect.value || (currentClass !== null && r.need.indexOf(currentClass) !== -1);
        r.opt.disabled = !allowed;
        if (r.btn) {
          r.btn.disabled = !allowed;
          r.btn.classList.toggle('pdp-seg-btn--unavailable', !allowed);
          r.btn.title = allowed ? '' : 'Only available in Class ' + r.need.join(' or ');
        }
        if (!allowed && r.select.value === r.opt.value) {
          r.select.value = '';
          r.select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }

    classSelect.addEventListener('change', sync);
    sync();
    document.body.dataset.pdpClassRestrictionWired = '1';
  }

  /* ------------------------------------------------------------------------
     Hardcoded copy of the Product Customizer app's own field-level
     conditional logic ("show field X" / "hide field Y" when some other
     field equals a given value).

     Why this exists alongside the app's own rules (which are NOT being
     disabled): the app's rule engine lives entirely in its own admin UI —
     opaque to anyone reading the theme code, and previously shown (this
     session) to be capable of silently failing to render a newly-added
     field if configured the wrong way. This block ports every existing
     rule, product by product, into plain, readable JS that lives in the
     theme itself, so the logic is inspectable, version-controlled and
     testable independently of the app. It runs in addition to the app's
     own rules, not instead of them — applying the same hide/show state
     twice is harmless (both use the identical DOM pattern below), so
     there is no conflict, only redundancy, on products the app still also
     controls.

     Data source: every product's `product_customizer_x` /
     `conditional_logic` metafield and its `product_customizer` field
     definitions, fetched from Shopify and mechanically translated from
     the app's internal numeric `product_option_id` references to the
     field `name` the DOM actually exposes (`select[name="properties[name]"]`).
     Three stale rules (referencing since-deleted fields, on two JUZO
     products) were dropped during translation rather than guessed at.

     JOBST Relief (below-knee/thigh-high/waist-high) note, added Sep 2026:
     the entry below was re-derived from a fresh read of the product's
     live `product_customizer_x` conditional_logic metafield — the
     original translation here was stale (it referenced a "Pantyhose -
     Beige Only" Model option that no longer exists on this product, now
     renamed "Waist High - Beige Only", so it could never match anything).
     Verified live on the storefront that, uncorrected, the Toe Variation
     field stayed visible and defaulted to "Open Toe" whenever Waist High
     was chosen with Class 1 or Class 2 (the app's own rule only hides it
     for Waist High + Class 3), and the Colours field likewise stayed on
     "Black" as a selectable choice for Waist High at Class 1/2 even
     though Waist High is Beige-only. Per Tim: Waist High never comes in
     Open Toe, at any class, so the Toe Variation rule below is
     intentionally broader than the app's own Class-3-only rule.

     Catalog-wide audit, Sep 2026 (per Tim: "fix all logic so only
     available items can be selected... do this for all products"):
     every active product's `product_customizer` field definitions and
     `product_customizer_x` conditional_logic metafield were pulled and
     diffed against this file.
       - Added the JOBST UltraSheer entry below — it has a real live app
         rule (Variation "Sensitive Thigh High" hides Toe Variation) that
         had no copy here at all.
       - Three products (JUZO Move, JUZO Intenso, JUZO Classic Seamless
         Leggings) have a `conditional_logic` metafield that references
         product_option_ids belonging to a DIFFERENT product (JUZO
         Dynamic's Model/Silicone Topband, JUZO Classic Seamless
         Armsleeve's Hand Garments) — ids that don't exist on their own
         fields at all. The app's own rule engine can't match anything on
         these three, so it's inert on both sides; no entry was added here
         since there's nothing real to mirror.
       - Every other product with a live conditional_logic rule already had
         a matching (or, for JOBST Relief, now-corrected) entry here.
       - Three additional gaps were option-level restrictions stated only
         in an option's own label, with no field-level conditional_logic
         (and no way for the app to express one) — fixed generically
         instead of per-product, in applyLengthRestrictedOptions's new
         "(X not available)" handling (JUZO Soft/Dynamic: Length "Extra
         Short" vs Model "Pantyhose"), the broadened looksLikeBelowKnee
         (JOBST FORMEN: "Knee High" now recognised as a below-knee length,
         so its "... Below Knee Only" sizes are cross-restricted), and the
         new applyClassRestrictedOptions block (SIGVARIS Cotton Essential,
         both variants: Length "Short (Only available in class 2)").
     Oct 2026: the rules below had fallen behind the option names and the
     rules in the app, and because this file keeps a field visible whenever
     none of ITS rules match, it was overriding the app and showing fields
     that should be hidden:
       - SIGVARIS Essential Classical: the two pantyhose lengths had
         " - Closed Toe Only - 4-6 weeks ETA" added to their names, so the
         toe choice showed for pantyhose (with Open Toes pre-selected).
       - VENOSAN 4000: the pantyhose option was renamed "Pantyhose Closed
         Toe Only", so Toe Variation and Silicone Topband showed for
         pantyhose.
       - VENOSAN 5000: the app also hides Silicone Topband for pantyhose.
       - VENOSAN 3000: the app also hides Toe Variation for thigh high.
     Conditions on a pantyhose option now use "startsWith" (see below), so
     adding a note to the end of an option's name no longer breaks them.
     WHEN AN OPTION IS RENAMED OR A RULE IS CHANGED IN THE APP, UPDATE THE
     MATCHING ENTRY HERE AS WELL.
     PRODUCT_RULES shape:
       { "<product handle>": [
           { "match": "all"|"any",
             "conditions": [ { "field": "<field name>", "is": "<exact option text, price suffix ignored>" },
                             { "field": "<field name>", "startsWith": "<start of the option text, any case>" }, ... ],
             "actions":    [ { "type": "show"|"hide", "field": "<field name>" }, ... ] },
           ...
         ], ... }
     ------------------------------------------------------------------------ */
  var PRODUCT_RULES = {"sigvaris-sheer-fashion-compression-stockings-and-pantyhose-for-females":[{"match":"any","conditions":[{"field":"Length","is":"Thigh High"},{"field":"Length","is":"Below Knee"}],"actions":[{"type":"show","field":"Below Knee & Thigh High size"}]},{"match":"any","conditions":[{"field":"Length","is":"Maternity Pantyhose "},{"field":"Length","is":"Pantyhose"}],"actions":[{"type":"show","field":"Pantyhose sizes"}]}],"sigvaris-essential-recovery-value-compression-stocking-for-males-and-females":[{"match":"any","conditions":[{"field":"Length","startsWith":"Pantyhose"}],"actions":[{"type":"hide","field":"Toe Variation"}]}],"sigvaris-semitransparent-magic":[{"match":"all","conditions":[{"field":"Model","is":"Below Knee"}],"actions":[{"type":"show","field":"Below Knee Length"}]},{"match":"any","conditions":[{"field":"Model","is":"Pantyhose"},{"field":"Model","is":"Thigh High"}],"actions":[{"type":"show","field":"Thigh & Pantyhose Lengths"}]},{"match":"all","conditions":[{"field":"Model","is":"Maternity Pantyhose "}],"actions":[{"type":"show","field":"Maternity Pantyhose Length"}]},{"match":"any","conditions":[{"field":"Model","is":"Maternity Pantyhose "},{"field":"Model","is":"Pantyhose"}],"actions":[{"type":"hide","field":"Silicone Topband"}]},{"match":"all","conditions":[{"field":"Class","is":"Class 1"},{"field":"Model","is":"Below Knee"}],"actions":[{"type":"hide","field":"Silicone Topband"}]}],"juzo-soft-stocking-male-and-female":[{"match":"all","conditions":[{"field":"Model","is":"Pantyhose"}],"actions":[{"type":"hide","field":"Silicone Topband"}]}],"juzo-dynamic-stocking-male-and-female":[{"match":"all","conditions":[{"field":"Model","is":"Pantyhose"}],"actions":[{"type":"hide","field":"Silicone Topband"}]}],"juzo-dynamic-armsleeve-male-and-female":[{"match":"all","conditions":[{"field":"Shoulder Cap","is":"With Shoulder Strap"}],"actions":[{"type":"hide","field":"Silicone Topband"}]}],"juzo-inspiration-stocking-male-and-female":[{"match":"any","conditions":[{"field":"Model","is":"Pantyhose"},{"field":"Model","is":"Maternity Pantyhose "}],"actions":[{"type":"hide","field":"Silicone Topband"}]}],"juzo-classic-seamless-armsleeve-male-and-female":[{"match":"all","conditions":[{"field":"Hand Garments","is":"Not required"}],"actions":[{"type":"hide","field":"Hand Gauntlet or Glove Size"}]}],"jobst-relief-stocking-male-female-below-knee-thigh-high-pantyhose":[{"match":"any","conditions":[{"field":"Model","is":"Waist High - Beige Only"},{"field":"Class","is":"Class 3 (CCL 3: 30 - 40 mmHg)"}],"actions":[{"type":"hide","field":"Colours","value":"Beige"}]},{"match":"all","conditions":[{"field":"Model","is":"Waist High - Beige Only"},{"field":"Class","is":"Class 3 (CCL 3: 30 - 40 mmHg)"}],"actions":[{"type":"hide","field":"Toe Variation","value":"Open Toe"}]}],"sigvaris-traditional-male-and-female-below-knee-thigh-high-pantyhose-maternity-pantyhose":[{"match":"any","conditions":[{"field":"Sizes","is":"XL (Knee High Only)"},{"field":"Sizes","is":"XL Plus (Knee High Only)"}],"actions":[{"type":"hide","field":"Model"},{"type":"hide","field":"Silicone Topband"}]}],"venosan®-3000-cotton-natural-look":[{"match":"any","conditions":[{"field":"Class","is":"Class 3 (CCL 3) OPEN TOE AND KNEE HIGH ONLY"}],"actions":[{"type":"hide","field":"Toe Variation"}]},{"match":"all","conditions":[{"field":"Class","is":"Class 3 (CCL 3) OPEN TOE AND KNEE HIGH ONLY"}],"actions":[{"type":"hide","field":"Variation"}]},{"match":"all","conditions":[{"field":"Variation","is":"Thigh High WITH Silicone Topband"}],"actions":[{"type":"hide","field":"Toe Variation"}]}],"venosan®-4000-climate-effect-microfibre-fine-and-stylish":[{"match":"any","conditions":[{"field":"Variation","startsWith":"Pantyhose"},{"field":"Variation","startsWith":"Maternity Pantyhose"},{"field":"Variation","is":"Thigh High WITH Silicone Topband"}],"actions":[{"type":"hide","field":"Silicone Topband"}]},{"match":"any","conditions":[{"field":"Variation","startsWith":"Pantyhose"},{"field":"Variation","startsWith":"Maternity Pantyhose"}],"actions":[{"type":"hide","field":"Toe Variation"}]}],"venosan®-5000-the-magic-of-silver-for-ideal-temperature-regulation":[{"match":"any","conditions":[{"field":"Variation","startsWith":"Pantyhose"}],"actions":[{"type":"hide","field":"Toe Variation"},{"type":"hide","field":"Silicone Topband"}]},{"match":"all","conditions":[{"field":"Variation","is":"Thigh High WITH Silicone Topband"}],"actions":[{"type":"hide","field":"Silicone Topband"}]},{"match":"any","conditions":[{"field":"Variation","is":"Below Knee"}],"actions":[{"type":"hide","field":"Silicone Topband"}]}],"venosan®-6000-classic-reliable-and-cost-effective":[{"match":"any","conditions":[{"field":"Variation","is":"Pantyhose (Classes 1 and 2 are Closed Toe, Class 3 is Open Toe)"}],"actions":[{"type":"hide","field":"Toe Variation"}]},{"match":"any","conditions":[{"field":"Class","is":"Class 3 (CCL3) (Beige and Open Toe Only)"}],"actions":[{"type":"hide","field":"Colour"},{"type":"hide","field":"Toe Variation"}]}],"venosan®-7000-natural-touch-high-stiffness-with-optimal-comfort-available-in-classes-1-2-and-3":[{"match":"all","conditions":[{"field":"Class","is":"Class 1 (CCL 1) Below Knee and Beige only"}],"actions":[{"type":"hide","field":"Variation"},{"type":"hide","field":"Colour"}]}],"jobst®-bella™-lite-circular-knit-ready-to-wear-rtw-class-2":[{"match":"all","conditions":[{"field":"Model","is":"Gauntlet"}],"actions":[{"type":"hide","field":"Length"}]}],"jobst-bella-strong-circular-knit-ready-to-wear-rtw-class-2-copy":[{"match":"all","conditions":[{"field":"Model","is":"Armsleeve"}],"actions":[{"type":"hide","field":"Gauntlet Size"}]},{"match":"all","conditions":[{"field":"Model","is":"Gauntlet"}],"actions":[{"type":"hide","field":"Size"},{"type":"hide","field":"Length"}]}],"jobst-ultrasheer-female-below-knee-thigh-high-pantyhose-black-natural":[{"match":"all","conditions":[{"field":"Variation","is":"Sensitive Thigh High"}],"actions":[{"type":"hide","field":"Toe Variation"}]}]};

  // CSO 15 Oct 2026: tidied product web addresses (VENOSAN 3000-7000 added 19 Oct 2026). Rules stay keyed by the
  // original handle; the new handle is mapped back to it here.
  var HANDLE_ALIASES = {"venosan-3000-cotton-compression-stockings": "venosan®-3000-cotton-natural-look", "venosan-4000-microfibre-compression-stockings": "venosan®-4000-climate-effect-microfibre-fine-and-stylish", "venosan-5000-silver-compression-stockings": "venosan®-5000-the-magic-of-silver-for-ideal-temperature-regulation", "venosan-6000-compression-stockings": "venosan®-6000-classic-reliable-and-cost-effective", "venosan-7000-natural-touch-compression-stockings": "venosan®-7000-natural-touch-high-stiffness-with-optimal-comfort-available-in-classes-1-2-and-3", "jobst-farrowwrap-thigh-wrap": "jobst®-farrowwrap®-foot-suitable-for-24-hour-wear-classes-2-and-3-tan-colour-only-copy", "jobst-farrowwrap-leg-wrap": "jobst®-farrowwrap®-leg-pieces-suitable-for-24-hour-wear-classes-2-and-3-tan-colour-only-copy-copy", "jobst-farrowwrap-knee-wrap": "jobst®-farrowwrap®-leg-pieces-suitable-for-24-hour-wear-classes-2-and-3-tan-colour-only-copy", "jobst-farrowwrap-foot-wrap": "jobst®-farrowwrap®-foot-suitable-for-24-hour-wear-classes-2-and-3", "jobst-bella-strong-circular-knit-rtw-class-2": "jobst-bella-strong-circular-knit-ready-to-wear-rtw-class-2-copy", "jobst-bella-lite-circular-knit-rtw-class-2": "jobst®-bella™-lite-circular-knit-ready-to-wear-rtw-class-2", "readywrap-knee-male-and-female": "venosan®-readywrap-calf-male-and-female-left-and-right-legs-copy", "bauerfeind-venotrain-cocoon-knee-high-class-2": "bauerfeind-venotrain-soft-compression-range-below-knee-thigh-high-and-pantyhose-classes-1-and-2-copy", "sigvaris-essential-classical-compression-stockings": "sigvaris-essential-recovery-value-compression-stocking-for-males-and-females", "juzo-classic-seamless-compression-leggings": "juzo-classic-seamless-stockings-male-and-female-premium-seamless-toe-cap", "sigvaris-compreflex-basic-liner": "compreflex-basic-liner-non-compressive-0reviews"};
  function getCurrentProductHandle() {
    var m = /\/products\/([^/?#]+)/.exec(window.location.pathname);
    if (!m) return null;
    var h = decodeURIComponent(m[1]);
    return HANDLE_ALIASES[h] || h;
  }

  // The app appends a price suffix like " (+$10.00)" to an option's
  // rendered text at display time; the stored rule values are the base
  // label without it (mirrors parseOptionText's price handling above, but
  // keeps the rest of the label — including any " - note" suffix — intact,
  // since that's part of the base label the app's own rules compare
  // against).
  function normalizeOptionText(text) {
    return (text || '')
      .replace(/\s*\([+-]\$[\d,]+(?:\.\d+)?\)\s*$/, '')
      .trim();
  }

  function applyProductCustomizerRules() {
    var handle = getCurrentProductHandle();
    var rules = handle && PRODUCT_RULES[handle];
    if (!rules || !rules.length) return;

    // The app can render more than one complete copy of the option fields
    // on a single page — each inside its OWN <form> that posts to
    // /cart/add (the same duplication documented above
    // applyRequiredAddonOptions, e.g. a second, off-screen copy used
    // elsewhere on the page). Bug found in the Sep 2026 catalog audit: this
    // function used to build ONE global field-name -> <select> map from
    // every matching select on the page regardless of which form it
    // belonged to, so on a product with two such forms it could end up
    // reading a condition's value from one form's copy while hiding (or
    // failing to hide) a card in a DIFFERENT form — observed live on JOBST
    // Relief, where selecting "Waist High" left the visible Toe
    // Variation/Colours fields showing and submittable in the actual
    // /cart/add payload despite the rule correctly matching. Fixed by
    // grouping selects by their owning <form> first and running the whole
    // match/hide pass independently, once per form.
    var byForm = []; // [{ form, fieldSelects: {name: select} }]
    Array.prototype.forEach.call(
      document.querySelectorAll('product-info .product-customizer-option select'),
      function (select) {
        var m = /^properties\[(.*)\]$/.exec(select.name || '');
        if (!m) return;
        var form = select.form || null;
        var group = null;
        for (var i = 0; i < byForm.length; i++) {
          if (byForm[i].form === form) { group = byForm[i]; break; }
        }
        if (!group) {
          group = { form: form, fieldSelects: {} };
          byForm.push(group);
        }
        group.fieldSelects[m[1]] = select;
      }
    );
    byForm.forEach(function (group) {
      applyProductCustomizerRulesToFieldSelects(group.fieldSelects, rules);
    });
  }

  // Rebuilt from the live DOM on every call (rather than wired once) so
  // that a field the app renders asynchronously — after this ran the
  // first time — still gets picked up: the app shows a loading state
  // before its fields appear, and fields can arrive at slightly
  // different times. This runs from the same MutationObserver that
  // re-runs enhanceAll(), so it's cheap and already re-invoked whenever
  // the app's markup changes. Operates on a single form's field map — see
  // applyProductCustomizerRules above for why there can be more than one.
  function applyProductCustomizerRulesToFieldSelects(fieldSelects, rules) {
    if (!Object.keys(fieldSelects).length) return; // app hasn't rendered its fields yet

    // Which action types (show/hide) ever target each field, across every
    // rule for this product — used to derive each field's natural default
    // state before any rule has matched.
    var actionTypesByField = {};
    rules.forEach(function (rule) {
      rule.actions.forEach(function (a) {
        actionTypesByField[a.field] = actionTypesByField[a.field] || {};
        actionTypesByField[a.field][a.type] = true;
      });
    });

    var fieldState = {}; // name -> { card, select }
    Object.keys(actionTypesByField).forEach(function (name) {
      var select = fieldSelects[name];
      if (!select) return; // field this rule targets isn't on the page (yet); skip safely
      var card = select.closest('.product-customizer-option');
      if (!card) return;
      // Stash the field's natural required-ness on the select itself, the
      // first time (and only the first time) we ever see this exact node,
      // so it survives however many times this function re-runs on it
      // without ever capturing our OWN previously-applied required=false.
      if (select.dataset.pdpOriginalRequired === undefined) {
        select.dataset.pdpOriginalRequired = select.required ? '1' : '0';
      }
      fieldState[name] = { card: card, select: select };
      watchRuleCard(card);
    });

    // Find the <option> whose value or (price-stripped) label matches `text`.
    function findOptionByText(select, text) {
      var want = (text || '').trim().toLowerCase();
      if (!want) return null;
      for (var i = 0; i < select.options.length; i++) {
        var o = select.options[i];
        if (!o.value) continue;
        if (o.value.trim().toLowerCase() === want) return o;
        if (normalizeOptionText(o.text).toLowerCase() === want) return o;
      }
      return null;
    }

    function setFieldVisible(name, visible, hiddenValue) {
      var st = fieldState[name];
      if (!st) return;
      // Oct 2026: each write to the card is skipped when the card is already
      // in that state. The cards are now watched for changes (watchRuleCard
      // below), and a write that changes nothing would still count as a
      // change and set off another pass.
      if (visible) {
        if (st.card.classList.contains('conditional-logic-hidden')) st.card.classList.remove('conditional-logic-hidden');
        if (st.card.style.display !== '') st.card.style.display = '';
        st.select.required = st.select.dataset.pdpOriginalRequired === '1';
      } else {
        if (!st.card.classList.contains('conditional-logic-hidden')) st.card.classList.add('conditional-logic-hidden');
        if (st.card.style.display !== 'none') st.card.style.display = 'none';
        st.select.required = false;
        // Hiding a field previously left its last-chosen (or default)
        // value sitting in the <select>, still selected and still
        // submitted in the /cart/add payload even though the field is no
        // longer visible or editable — this is the actual mechanism
        // behind the original bug report (a hidden Toe Variation still
        // carrying "Open Toe" into the cart for Waist High). Clearing it
        // here, and dispatching 'change' so the picker UI and any other
        // listeners stay in sync, ensures a hidden field never
        // contributes an invalid/stale value to the order.
        // A hide action may carry a `value` (e.g. {type:"hide", field:"Colours",
        // value:"Beige"}): the only choice that exists for this combination.
        // Instead of clearing the field (which left the property blank on
        // the order and made fulfilment infer it), select that option so
        // the cart line still records it. Without a `value`, clear as before.
        var target = hiddenValue ? findOptionByText(st.select, hiddenValue) : null;
        var wanted = target ? target.value : '';
        if (st.select.value !== wanted) {
          st.select.value = wanted;
          // pdpOwnChange (Oct 2026) tells the any-option listener further
          // down that this 'change' came from here, not from the shopper.
          pdpOwnChange++;
          try {
            st.select.dispatchEvent(new Event('change', { bubbles: true }));
          } finally {
            pdpOwnChange--;
          }
        }
      }
    }

    function conditionMatches(cond) {
      var select = fieldSelects[cond.field];
      if (!select) return false;
      var selectedText = select.options[select.selectedIndex]
        ? select.options[select.selectedIndex].text
        : '';
      var actual = normalizeOptionText(selectedText);
      // "startsWith" (Oct 2026): compares only the START of the option's
      // text, ignoring upper/lower case, so the rule keeps working when a
      // note is added to the end of an option's name in the app.
      if (cond.startsWith) {
        return actual.toLowerCase().indexOf(String(cond.startsWith).trim().toLowerCase()) === 0;
      }
      return actual === (cond.is || '').trim();
    }

    function evaluate() {
      // Start every field at its natural default: hidden only if every
      // rule that targets it is a "show" rule (i.e. it's a field meant to
      // stay hidden until some condition reveals it); visible otherwise.
      var visibility = {};
      var hiddenValues = {}; // name -> value a matched hide rule wants selected
      Object.keys(actionTypesByField).forEach(function (name) {
        if (!fieldState[name]) return;
        var types = actionTypesByField[name];
        visibility[name] = !(types.show && !types.hide);
      });

      rules.forEach(function (rule) {
        var matched = rule.match === 'any'
          ? rule.conditions.some(conditionMatches)
          : rule.conditions.every(conditionMatches);
        if (!matched) return;
        rule.actions.forEach(function (action) {
          if (fieldState[action.field]) {
            visibility[action.field] = (action.type === 'show');
            if (action.type === 'hide') hiddenValues[action.field] = action.value || null;
          }
        });
      });

      Object.keys(visibility).forEach(function (name) {
        setFieldVisible(name, visibility[name], visibility[name] ? null : hiddenValues[name]);
      });
    }

    // Re-evaluate whenever any field referenced in a condition changes.
    // Guarded per-select (not per-product) so a freshly-rendered select
    // node always gets its own listener, while an already-wired one never
    // accumulates duplicates.
    var conditionFieldNames = {};
    rules.forEach(function (rule) {
      rule.conditions.forEach(function (c) {
        conditionFieldNames[c.field] = true;
      });
    });
    Object.keys(conditionFieldNames).forEach(function (name) {
      var select = fieldSelects[name];
      if (select && !select.dataset.pdpRulesListenerAttached) {
        // Bound to the top-level function, not to this call's own
        // `evaluate` closure: if this particular call happened while the
        // app was still rendering a later field (e.g. one that shows up
        // after a short loading state), a listener closed over THIS
        // call's incomplete fieldState would permanently miss that field
        // on every future user interaction, even though a later
        // MutationObserver-triggered call rebuilds fieldState correctly.
        // Re-running the whole function on every change avoids that: it
        // always re-scans the live DOM first.
        select.addEventListener('change', applyProductCustomizerRulesDebounced);
        select.dataset.pdpRulesListenerAttached = '1';
      }
    });

    evaluate();
  }

  // Race condition found live on JOBST Relief during the Sep 2026 audit,
  // AFTER the dual-form fix above: the Product Customizer app registers
  // its own 'change' handling on these same selects and, on some changes,
  // asynchronously re-renders a field's wrapper — which overwrites its
  // className/style wholesale (not via classList.add/remove, so it can't
  // be intercepted) — shortly AFTER our synchronous evaluate() above has
  // already correctly hidden it. Confirmed via direct instrumentation:
  // 'conditional-logic-hidden' gets added and style.display set to
  // 'none', then reverted within roughly 100-300ms; sometimes the app's
  // own handling runs first and our hide never visibly takes at all. This
  // is separate from (though likely worsened by) the type:json metafield
  // corruption on this product's Model field logged elsewhere in this
  // file — that crash happens in the app's own per-field handler
  // registration and plausibly makes its re-render timing/behaviour more
  // erratic. Rather than depend on winning a race with a third-party
  // app's undocumented internals, re-apply our own evaluation a few times
  // after each change until the app's own async work has almost
  // certainly settled, so whichever ran last is always ours.
  var pdpReapplyTimers = [];
  function applyProductCustomizerRulesDebounced() {
    applyProductCustomizerRules();
    pdpReapplyTimers.forEach(function (t) { clearTimeout(t); });
    pdpReapplyTimers = [150, 400, 900, 1800, 3000].map(function (delay) {
      return setTimeout(applyProductCustomizerRules, delay);
    });
  }

  // Oct 2026: keeping a hidden field hidden.
  // The app applies its OWN copy of the rules every time any option changes,
  // and again while it starts up. Where its copy and the one above disagree,
  // it put back a field this file had just hidden. Seen on SIGVARIS Essential
  // Classical: the app's rule there still names the old pantyhose options, so
  // the toe choice came back for pantyhose as soon as a colour or a size was
  // picked (and, on a link from the sizing calculator, came back with "Open
  // Toes" filled in). The rules above used to be applied only when an option
  // that a rule depends on changed. Two additions close the gap:
  //
  // 1. Every card a rule controls is watched. The moment anything changes
  //    whether it is shown, the rules above are applied again, before the
  //    page is redrawn, so the shopper never sees the field come back.
  var pdpCardWatcher = null;
  var pdpWatcherRuns = [];
  function watchRuleCard(card) {
    if (!window.MutationObserver || card.pdpRuleWatched) return;
    card.pdpRuleWatched = true;
    if (!pdpCardWatcher) {
      pdpCardWatcher = new MutationObserver(function () {
        // Safety: never go round in circles with the app. A shopper's
        // choice costs two to four passes. Sixty inside two seconds means
        // something keeps undoing this file's work, so stand back until it
        // stops (the timers above still apply the rules after each choice).
        var now = Date.now();
        pdpWatcherRuns = pdpWatcherRuns.filter(function (t) { return now - t < 2000; });
        if (pdpWatcherRuns.length >= 60) return;
        pdpWatcherRuns.push(now);
        applyProductCustomizerRules();
      });
    }
    pdpCardWatcher.observe(card, { attributes: true, attributeFilter: ['class', 'style'] });
  }

  // 2. The rules are also applied after ANY option changes. This listener is
  //    on the document, so it runs after the app's own handler, which sits
  //    on the options form, has finished.
  var pdpOwnChange = 0;      // above zero while this file is sending its own 'change'
  var pdpAnyChangeDepth = 0;
  document.addEventListener('change', function (event) {
    var field = event.target;
    if (!field || !field.closest || !field.closest('product-info .product-customizer-option')) return;
    var handle = getCurrentProductHandle();
    if (!handle || !PRODUCT_RULES[handle]) return;
    if (pdpAnyChangeDepth > 5) return; // safety: never nest without end
    pdpAnyChangeDepth++;
    try {
      if (pdpOwnChange) {
        // This file changed a hidden field's value and said so. Apply the
        // rules once more, but do not start the follow-up timers again.
        applyProductCustomizerRules();
      } else {
        applyProductCustomizerRulesDebounced();
      }
    } finally {
      pdpAnyChangeDepth--;
    }
  });

  // The Product Customizer app renders its fields asynchronously (there is
  // a visible "loading" state before them), so watch for them to appear
  // rather than assuming they exist at DOMContentLoaded.
  function init() {
    enhanceAll();
    applyLengthRestrictedOptions();
    applyRequiredAddonOptions();
    applyClassRestrictedOptions();
    applyProductCustomizerRules();
    var target = document.querySelector('product-info .product__app-block') || document.body;
    new MutationObserver(function () {
      enhanceAll();
      applyLengthRestrictedOptions();
      applyRequiredAddonOptions();
      applyClassRestrictedOptions();
      applyProductCustomizerRules();
    }).observe(target, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();